import {
  isLawType,
  LABOR_LAW,
  type LawType,
  resolveSelectedDomainForCapabilities,
} from '@dokia/domain-model';
import {useQueryClient} from '@tanstack/react-query';
import {
  createContext,
  type Dispatch,
  type PropsWithChildren,
  useContext,
  useEffect,
  useReducer,
} from 'react';

import {getAccessTokenCapabilities} from '../auth/accessTokenCapabilities';
import {performAppLogout} from '../auth/performAppLogout';
import {
  refreshAuthSessionAccessToken,
  setAuthSessionAccessTokenRefreshedCallback,
  setAuthSessionExpiredCallback,
} from '../clients/authSession';
import {setServerClientAccessToken} from '../clients/server';
import {setStrapiClientAccessToken} from '../clients/strapi';
import {type StoreAction, storeReducer, type StoreState} from './storeReducer';

const StoreStateContext = createContext<StoreState | undefined>(undefined);
const StoreDispatchContext = createContext<
  Dispatch<StoreAction> | undefined
>(undefined);

const initStore: StoreState = {
  accessToken: undefined,
  isAuthenticated: false,
  isInitializing: true,
  selectedDomain: LABOR_LAW,
  user: undefined,
};

export const LS_SELECTED_DOMAIN = 'dokia_selected_domain';
const getStoredSelectedDomain = (
): LawType => {
  if (import.meta.env.SSR) {
    return LABOR_LAW;
  }

  const storedSelectedDomain = localStorage.getItem(LS_SELECTED_DOMAIN);

  return storedSelectedDomain && isLawType(storedSelectedDomain)
    ? storedSelectedDomain
    : LABOR_LAW;
};

const createInitialStoreState = (): StoreState => ({
  ...initStore,
  selectedDomain: getStoredSelectedDomain(),
});

export const StoreProvider = ({children}: PropsWithChildren) => {
  const [state, dispatch] = useReducer(
    storeReducer,
    undefined,
    createInitialStoreState,
  );
  const queryClient = useQueryClient();

  useEffect(() => {
    const handleSessionExpired = () => {
      performAppLogout(queryClient, dispatch);
    };

    const handleAccessTokenRefreshed = (accessToken: string) => {
      setServerClientAccessToken(accessToken);
      setStrapiClientAccessToken(accessToken);
      dispatch({
        payload: {accessToken},
        type: 'SET_APP_LOGIN',
      });
    };

    setAuthSessionExpiredCallback(handleSessionExpired);
    setAuthSessionAccessTokenRefreshedCallback(handleAccessTokenRefreshed);

    return () => {
      setAuthSessionExpiredCallback(undefined);
      setAuthSessionAccessTokenRefreshedCallback(undefined);
    };
  }, [dispatch, queryClient]);

  useEffect(() => {
    const apiBase =
      (import.meta.env.VITE_API_URL as string | undefined) ?? '';
    // Route boot through the shared refresh mutex + cross-tab lock.
    refreshAuthSessionAccessToken({baseUrl: `${apiBase}/webapp`})
      .then(accessToken => {
        setServerClientAccessToken(accessToken);
        setStrapiClientAccessToken(accessToken);
        dispatch({
          payload: {accessToken},
          type: 'SET_APP_LOGIN',
        });
      })
      .catch(() => {
        dispatch({type: 'SET_APP_INITIALIZED'});
      });
  }, [dispatch]);

  useEffect(() => {
    if (!state.accessToken || !state.isAuthenticated) {
      return;
    }

    const userCapabilities = getAccessTokenCapabilities(state.accessToken);
    const resolvedSelectedDomain = resolveSelectedDomainForCapabilities(
      userCapabilities,
      state.selectedDomain,
    );

    if (resolvedSelectedDomain !== state.selectedDomain) {
      dispatch({
        payload: {selectedDomain: resolvedSelectedDomain},
        type: 'SET_SELECTED_DOMAIN',
      });
    }
  }, [
    dispatch,
    state.accessToken,
    state.isAuthenticated,
    state.selectedDomain,
  ]);

  useEffect(() => {
    localStorage.setItem(LS_SELECTED_DOMAIN, state.selectedDomain);
  }, [state.selectedDomain]);

  return (
    <StoreStateContext.Provider value={state}>
      <StoreDispatchContext.Provider value={dispatch}>
        {children}
      </StoreDispatchContext.Provider>
    </StoreStateContext.Provider>
  );
};

export const useStoreContext = () => {
  const storeContext = useContext(StoreStateContext);
  const dispatchContext = useContext(StoreDispatchContext);

  if (!storeContext || !dispatchContext) {
    throw new Error('useStoreContext must be used within a StoreProvider');
  }

  return [
    storeContext,
    dispatchContext,
  ] satisfies [StoreState, Dispatch<StoreAction>];
};
